import { z } from 'zod';
import { ServiceIdSchema } from './common';
import { LocaleSchema } from './specialization';

/**
 * What a therapist offers, and for how much.
 *
 * WHY THIS IS A LIST AND NOT A SINGLE RATE
 * A therapist does not do one thing at one price. Psychotherapy and coaching
 * are different work, take different preparation, and are worth different
 * money — and a client choosing between them is making a real decision, not
 * picking a variant. `TherapistProfile.hourlyRate` remains only as the
 * fallback used for sorting when a therapist has not configured anything yet;
 * everything a client can actually book is a row here.
 *
 * The catalogue of service TYPES (name, duration) is admin-managed and shared,
 * so "Ψυχοθεραπεία, 50 λεπτά" means the same thing on every profile. What the
 * therapist controls is the price and whether they offer it at all.
 */

/** One bookable service type from the shared catalogue. */
export const ServiceCatalogueItemSchema = z.object({
  serviceId: ServiceIdSchema,
  slug: z.string(),
  /** Resolved for the requested locale, falling back to the slug. */
  name: z.string(),
  description: z.string().nullable(),
  durationMinutes: z.number().int().positive(),
  categorySlug: z.string(),
});
export type ServiceCatalogueItem = z.infer<typeof ServiceCatalogueItemSchema>;

export const ServiceCatalogueResponseSchema = z.object({
  services: z.array(ServiceCatalogueItemSchema),
});
export type ServiceCatalogueResponse = z.infer<typeof ServiceCatalogueResponseSchema>;

/** A service this therapist offers, with their price for it. */
export const TherapistServiceOfferSchema = z.object({
  serviceId: ServiceIdSchema,
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  durationMinutes: z.number().int().positive(),
  /** Decimal serialized as a string, e.g. "50.00" — never a float. */
  price: z.string(),
  currency: z.string().length(3),
  /**
   * Inactive offers stay on the row rather than being deleted, so a therapist
   * who pauses a service for a month gets their price back when they resume
   * instead of having to remember it.
   */
  isActive: z.boolean(),
});
export type TherapistServiceOffer = z.infer<typeof TherapistServiceOfferSchema>;

export const TherapistServicesResponseSchema = z.object({
  services: z.array(TherapistServiceOfferSchema),
});
export type TherapistServicesResponse = z.infer<typeof TherapistServicesResponseSchema>;

/**
 * PUT /api/v1/therapist/services — create or update one offer.
 *
 * Price is taken as a string and validated as money: a number here would let
 * 50.10 arrive as 50.099999999999994, and this is what someone gets charged.
 */
export const UpsertTherapistServiceRequestSchema = z.object({
  serviceId: ServiceIdSchema,
  price: z
    .string()
    .regex(/^\d{1,6}(\.\d{1,2})?$/, 'Η τιμή πρέπει να είναι αριθμός με έως δύο δεκαδικά, π.χ. 50 ή 49.50')
    .refine((v) => Number(v) > 0, 'Η τιμή πρέπει να είναι μεγαλύτερη από μηδέν'),
  currency: z.string().length(3).default('EUR'),
  isActive: z.boolean().default(true),
});
export type UpsertTherapistServiceRequest = z.infer<typeof UpsertTherapistServiceRequestSchema>;

// ── Public therapist profile ──────────────────────────────────────────

/**
 * GET /api/v1/matching/therapists/:therapistUserId
 *
 * Deliberately public. A client should be able to read who they would be
 * talking to — bio, qualifications, what they offer, what it costs — before
 * being asked for anything at all. Requiring an account to look is how a
 * platform that promises "no commitment" quietly stops meaning it.
 *
 * Only APPROVED therapists are reachable; a suspended or pending profile
 * answers 404, the same as one that never existed.
 */
export const PublicTherapistProfileSchema = z.object({
  therapistProfileId: z.string().cuid(),
  therapistUserId: z.string().cuid(),
  displayName: z.string().nullable(),
  headline: z.string().nullable(),
  bio: z.string().nullable(),
  /** True when the requested locale had no translation and Greek was shown. */
  profileTranslationMissing: z.boolean(),
  professionalType: z.enum(['PSYCHOLOGIST', 'COUNSELOR']),
  photoUrl: z.string().nullable(),
  introVideoUrl: z.string().nullable(),
  languagesSpoken: z.array(z.string()),
  yearsOfExperience: z.number().int().nullable(),
  therapyStyle: z.string().nullable(),
  /** ISO-3166 alpha-2 of the issuing authority — evidence of a real licence, not an address. */
  licenseCountry: z.string().nullable(),
  specializations: z.array(z.object({ slug: z.string(), name: z.string() })),
  caseTags: z.array(z.string()),
  services: z.array(TherapistServiceOfferSchema),
  ratingAverage: z.number(),
  reviewCount: z.number().int(),
});
export type PublicTherapistProfile = z.infer<typeof PublicTherapistProfileSchema>;

export const PublicTherapistProfileResponseSchema = z.object({
  therapist: PublicTherapistProfileSchema,
});
export type PublicTherapistProfileResponse = z.infer<typeof PublicTherapistProfileResponseSchema>;

export const PublicTherapistProfileQuerySchema = z.object({
  locale: LocaleSchema.default('el'),
});
export type PublicTherapistProfileQuery = z.infer<typeof PublicTherapistProfileQuerySchema>;
